/**
 * Utility functions for handling storage and Firestore paths in the vault system
 */

import { buildStoragePath } from '../constants/paths';
import { ACCESS_LEVELS } from './accessControl';
import { getFirestore, getDoc, doc, setDoc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { checkUserPermissions } from './permissionUtils';
import logger from './logger';

/**
 * Get the Firebase Storage path for a file
 * @param {string} userId - The user's ID
 * @param {string} familyCode - The family code (optional)
 * @param {string} collectionType - The type of collection (photos, documents, etc.)
 * @param {string} fileId - The unique file ID
 * @param {string} storageArea - The storage area (personal, family, vault)
 * @returns {string} The complete storage path
 */
export const getStoragePath = (userId, familyCode, collectionType, fileId, storageArea) => {
  return buildStoragePath({
    userId,
    familyCode,
    collectionType,
    storageArea,
    fileId
  });
};

/**
 * Get the Firestore collection path
 * @param {string} userId - The user's ID
 * @param {string} familyCode - The family code (optional)
 * @param {string} collectionType - The type of collection (photos, documents, etc.)
 * @param {string} storageArea - The storage area (personal, family, vault)
 * @returns {string} The complete Firestore collection path
 */
export const getFirestorePath = (userId, familyCode, collectionType, storageArea) => {
  // Validate required parameters
  if (!userId) {
    throw new Error('User ID is required');
  }

  if (!collectionType) {
    throw new Error('Collection type is required');
  }

  if (!storageArea) {
    throw new Error('Storage area is required');
  }

  // Validate family code for family/vault access
  if ((storageArea === ACCESS_LEVELS.FAMILY || storageArea === ACCESS_LEVELS.VAULT) && !familyCode) {
    throw new Error('Family code is required for family or vault access');
  }

  // Construct path based on storage area
  switch (storageArea) {
    case ACCESS_LEVELS.PERSONAL:
      return `users/${userId}/${collectionType}`;
      
    case ACCESS_LEVELS.FAMILY:
      return `families/${familyCode}/${collectionType}`;
      
    case ACCESS_LEVELS.VAULT:
      return `families/${familyCode}/vault/${collectionType}`;
      
    default:
      throw new Error(`Invalid storage area: ${storageArea}`);
  }
};

/**
 * Create the document data object for Firestore
 * @param {Object} file - The file object
 * @param {string} userId - The user's ID
 * @param {string} familyCode - The family code (optional)
 * @param {string} currentPath - The current path in the folder structure
 * @param {string} downloadURL - The download URL from Storage
 * @param {string} storageArea - The storage area (personal, family, vault)
 * @returns {Object} The document data for Firestore
 */
export const createDocumentData = (file, userId, familyCode, currentPath, downloadURL, storageArea) => {
  // Normalize storage area for vault types
  const normalizedArea = ['documents', 'photos', 'records'].includes(storageArea) ? 'vault' : storageArea;

  return {
    name: file.name,
    type: 'file',
    contentType: file.type,
    size: file.size,
    url: downloadURL,
    path: currentPath || '',
    fullPath: `${currentPath || ''}/${file.name}`,
    userId: userId,
    familyCode: normalizedArea === 'family' ? familyCode : null,
    isPersonal: normalizedArea === 'personal',
    storageArea: normalizedArea,
    vaultType: ['documents', 'photos', 'records'].includes(storageArea) ? storageArea : null,
    createdAt: new Date(),
    updatedAt: new Date(),
  };
};

/**
 * Check if a user has access to a specific storage area
 * @param {Object} user - The user object
 * @param {string} storageArea - The storage area to check
 * @returns {boolean} Whether the user has access
 */
export const hasStorageAccess = async (user, storageArea) => {
  if (!user) return false;

  const db = getFirestore();
  try {
    // Get user data to check relationship
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) return false;
    
    const userData = userDoc.data();

    switch (storageArea) {
      case ACCESS_LEVELS.PERSONAL:
        return true; // All authenticated users have access to personal storage
        
      case ACCESS_LEVELS.FAMILY:
        return !!userData.familyCode; // User must have a family code
        
      case ACCESS_LEVELS.VAULT:
        // Check relationship-based access for vault
        return !!userData.familyCode && (
          userData.relationship === 'Immediate Family' ||
          userData.relationship === 'Elders' ||
          userData.relationship === 'Extended Family'
        );
        
      case ACCESS_LEVELS.DOCUMENTS:
      case ACCESS_LEVELS.PHOTOS:
        return true; // Basic access for documents and photos
        
      default:
        return false;
    }
  } catch (error) {
    logger.error('Error checking storage access:', error);
    return false;
  }
};

/**
 * Initialize a user's vault
 */
export const initializeUserVault = async (userId, familyCode) => {
  if (!userId || !familyCode) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    const vaultDoc = await getDoc(vaultRef);

    if (!vaultDoc.exists()) {
      await setDoc(vaultRef, {
        owner: userId,
        familyCode,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true
      });
    }
  } catch (error) {
    logger.error('Error initializing user vault:', error);
    throw error;
  }
};

/**
 * Get a user's vault configuration
 */
export const getUserVaultConfig = async (userId) => {
  if (!userId) return null;

  const db = getFirestore();
  try {
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    const vaultDoc = await getDoc(vaultRef);

    if (!vaultDoc.exists()) return null;
    return vaultDoc.data();
  } catch (error) {
    logger.error('Error getting user vault config:', error);
    return null;
  }
};

/**
 * Share vault access with family members
 */
export const shareVaultAccess = async (userId, memberIds) => {
  if (!userId || !memberIds?.length) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    // Get user's vault config
    const vaultConfig = await getUserVaultConfig(userId);
    if (!vaultConfig) throw new Error('Vault not initialized');

    // Update each member's vault access
    for (const memberId of memberIds) {
      const memberRef = doc(db, 'users', memberId);
      await updateDoc(memberRef, {
        'vaultAccess': arrayUnion({
          vaultId: userId,
          grantedAt: new Date(),
          grantedBy: userId
        })
      });
    }

    // Update vault sharing settings
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    await updateDoc(vaultRef, {
      sharedWith: arrayUnion(...memberIds),
      updatedAt: new Date()
    });
  } catch (error) {
    logger.error('Error sharing vault access:', error);
    throw error;
  }
};

/**
 * Remove vault access from family members
 */
export const removeVaultAccess = async (userId, memberIds) => {
  if (!userId || !memberIds?.length) {
    throw new Error('Missing required parameters');
  }

  const db = getFirestore();
  try {
    // Remove access from each member
    for (const memberId of memberIds) {
      const memberRef = doc(db, 'users', memberId);
      await updateDoc(memberRef, {
        'vaultAccess': arrayRemove({
          vaultId: userId
        })
      });
    }

    // Update vault sharing settings
    const vaultRef = doc(db, 'users', userId, 'vault', 'config');
    await updateDoc(vaultRef, {
      sharedWith: arrayRemove(...memberIds),
      updatedAt: new Date()
    });
  } catch (error) {
    logger.error('Error removing vault access:', error);
    throw error;
  }
};

/**
 * Check if a user has access to a vault
 */
export const hasVaultAccess = async (userId, vaultId) => {
  if (!userId || !vaultId) return false;

  try {
    // Check if user is vault owner
    if (userId === vaultId) return true;

    // Check if user has vault access through permissions
    return await checkUserPermissions(userId, 'vault_access');
  } catch (error) {
    logger.error('Error checking vault access:', error);
    return false;
  }
}; 